/**
 * noGamDiagnosis.js — why does Slots have nothing to show? Turns the captured
 * rows into a short, evidence-backed explanation instead of a blank page.
 *
 * The causes are the ones this tool has actually hit:
 *  - no ad traffic yet: the app hasn't opened a screen that loads an ad;
 *  - demand without GAM: Prebid/APS fired but GAM didn't reach the proxy —
 *    device-side QUIC (HTTP/3) bypassing the MITM; BLOCK_QUIC=true on the proxy
 *    fixes it (memory: empty-lifecycle-diagnosis);
 *  - USB capture: the logcat tap only ever sends GAM URLs.
 * Pure; never throws.
 */

const GAM_RE = /doubleclick\.net\/gampad\/(live\/)?ads/i;
const DEMAND_TYPES = new Set(['prebid', 'amazon', 'amazon_video', 'sdk_bidding']);

function hostOf(url) {
  try { return new URL(url).host; } catch { return ''; }
}

export function diagnoseNoGam(rows) {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const out = { total: list.length, gam: 0, demand: 0, usb: 0, topHosts: [], cause: 'no_ads_yet' };
  try {
    const hosts = new Map();
    for (const r of list) {
      const url = String(r.url || '');
      if (GAM_RE.test(url) || r.requestType === 'google' || r.requestType === 'gam_video') out.gam++;
      if (DEMAND_TYPES.has(r.requestType)) out.demand++;
      if (r.captureSource === 'logcat') out.usb++;
      const h = hostOf(url);
      if (h) hosts.set(h, (hosts.get(h) || 0) + 1);
    }
    out.topHosts = [...hosts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([host, count]) => ({ host, count }));
    if (out.usb && out.usb === list.length) out.cause = 'usb_waiting';
    else if (out.demand > 0) out.cause = 'demand_without_gam';
  } catch { /* best effort */ }
  return out;
}

export const NO_GAM_ADVICE = {
  no_ads_yet: 'None of these are ad requests. Open a screen in the app that shows ads (home, an article, a video) and scroll to the ad slot — slots appear as soon as the first GAM request fires.',
  demand_without_gam: 'Header bidding fired (Prebid/APS) but no GAM request reached the proxy. That is usually the device sending GAM over QUIC (HTTP/3), which bypasses the proxy — set BLOCK_QUIC=true in the proxy .env and restart the app.',
  usb_waiting: 'USB capture only sends GAM ad requests. Nothing yet means the app has not made an ad call — open a screen that shows ads.',
};

/**
 * Some GAM requests arrived, but far fewer than the Prebid auctions that should
 * precede them — part of the GAM traffic likely went over QUIC and bypassed the
 * proxy, so Slots is missing slots. Counts publisher GAM requests only (an IU of
 * 2+ segments; single-segment IUs are third-party creatives). USB capture sends
 * no Prebid rows, so it never trips this. Pure; never throws.
 */
export function partialGamCapture(rows) {
  const out = { prebid: 0, amazon: 0, gam: 0, partial: false };
  try {
    for (const r of Array.isArray(rows) ? rows : []) {
      if (!r) continue;
      const t = String(r.type || r.requestType || '').toLowerCase();
      if (t === 'prebid') out.prebid++;
      else if (t === 'amazon') out.amazon++;
      else if (t === 'google') {
        const iu = String((r.validData && r.validData.adUnit) || r.id || '');
        if (iu.split('/').filter(Boolean).length >= 2) out.gam++;
      }
    }
    out.partial = out.gam > 0 && out.prebid > 0 && out.gam < out.prebid * 0.6;
  } catch { /* best effort */ }
  return out;
}
